import React, { useState } from 'react';
import {
  IAvailableWorkflowActions,
  IFiguresAndTablesFile
} from '@publishing-engine/domain/models';
import { Flex } from '@chakra-ui/react';
import { FormFieldFiguresInput } from '@publishing-engine/domain/types/article';
import { FieldHeader } from '../common/FieldHeader';
import FiguresUpdateModal from './FiguresUpdateModal';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { FiguresDiffModal } from './FiguresDiffModal';
import FiguresDisplay from './FiguresDisplay';
import { FiguresEmptyState } from './FiguresEmptyState';

interface Props {
  formFieldDefinition: FormFieldFiguresInput;
  value: IFiguresAndTablesFile;
  previousValue?: IFiguresAndTablesFile;
  isEditable: boolean;
  onChange: (value: IFiguresAndTablesFile) => void;
  submissionContextId: string;
  availableActions: IAvailableWorkflowActions;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
}

export const Figures: React.FC<Props> = ({
  formFieldDefinition,
  value,
  previousValue,
  isEditable,
  submissionContextId,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated, fieldsResponse } =
    useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const hasValue = !!value?.length;

  return (
    <Flex flexDirection={'column'} gap={2} id="Figures">
      {isEditing && (
        <FiguresUpdateModal
          isOpen={isEditing}
          formField={formFieldDefinition}
          onClose={() => setIsEditing(false)}
          value={value}
          onSubmit={value => {
            setIsEditing(false);
            onChange(value.fileSet?.Figures ?? []);
          }}
          submissionContextId={submissionContextId}
          submissionFields={fieldsResponse?.fields}
        />
      )}

      {isUpdated && showDiffModal ? (
        <FiguresDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={`${formFieldDefinition.params.labels.main} (${
          value?.length ?? 0
        })`}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {hasValue ? (
        <FiguresDisplay figures={value} />
      ) : (
        <FiguresEmptyState fieldLabels={formFieldDefinition.params.labels} />
      )}
    </Flex>
  );
};
